import { useReservationsBusinessCaseListFilterOptions } from '@/api/generated/reservations/reservations';
import { customInstance } from '@/api/customInstance';
import {
  reservationsBusinessCaseList,
  reservationsBusinessCaseListStats,
} from '@/api/generated/reservations/reservations';
import type {
  BusinessCaseListFilterOptionsResponse,
  BusinessCaseListItem,
  BusinessCaseListStatsResponse,
  CodebookOption,
} from '@/api/generated/schemas';
import {
  col,
  defineListPage,
  downloadBlob,
  filter,
  LIST_EXPORT_MAX_ROWS,
  type ListEnumOptionsSource,
  type ListExportColumn,
  type ListQueryRequest,
} from '@/shared/ui/list-page';

import { partnerSelector, type PartnerRef } from '../../components/PartnerSelector';

/** Lokální datum → YYYY-MM-DD (bez UTC posunu). */
function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Zdroj options enum filtru nad společným endpointem filter-options — jeden dotaz
 * (React Query cache) obslouží všechny čtyři číselníkové filtry.
 */
function codebookOptions(
  select: (response: BusinessCaseListFilterOptionsResponse) => CodebookOption[]
): ListEnumOptionsSource {
  return {
    useOptions() {
      const query = useReservationsBusinessCaseListFilterOptions();
      return {
        options: (query.data ? select(query.data) : []).map((option) => ({
          id: String(option.id),
          label: option.name,
          code: option.code ?? null,
        })),
        isLoading: query.isLoading,
      };
    },
  };
}

/**
 * Šablona ListPage obchodních případů — deklaruje sloupce, filtry a chování;
 * kompletní chování seznamu dodává engine `@/shared/ui/list-page`.
 * Klíče filtrů a řazení odpovídají BE `BusinessCaseListSpec`.
 */
const STATS_NUMBER = new Intl.NumberFormat('cs-CZ', { maximumFractionDigits: 0 });

/** Export OP do XLSX — Orval u binárky vrací string, voláme proto customInstance s blobem. */
function exportBusinessCases(
  request: ListQueryRequest,
  columns: ListExportColumn[],
  maxRows: number,
  signal?: AbortSignal
): Promise<Blob> {
  return customInstance<Blob>({
    url: '/api/reservations/business-cases/list/export',
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: { request, columns, maxRows },
    responseType: 'blob',
    signal,
  });
}

const businessCaseList = defineListPage<BusinessCaseListItem, BusinessCaseListStatsResponse>({
  key: 'reservations.business-cases',
  i18n: { namespace: 'reservations' },
  api: {
    list: (request, signal) => reservationsBusinessCaseList(request, signal),
    stats: (request, signal) => reservationsBusinessCaseListStats(request, signal),
    export: exportBusinessCases,
  },
  rowKey: (item) => item.id,
  defaultSort: { column: 'number', desc: true },
  fulltext: { placeholder: (t) => t('businessCaseList.search') },
  exportFileName: (t) => t('businessCaseList.exportFile'),
  // Lehký náhled (placeholder) — reálný detail OP doplní vlastník agendy. Čte data z řádku.
  preview: (item, t) => (
    <div className="gap-phx-4 flex flex-col">
      <div className="text-phx-h3 font-semibold">{item.productName ?? '—'}</div>
      {(
        [
          [t('businessCaseList.cols.number'), String(item.number)],
          [t('businessCaseList.cols.customer'), item.customerName ?? '—'],
          [t('businessCaseList.cols.checkOutState'), item.checkOutStateName ?? '—'],
          [t('businessCaseList.cols.totalPrice'), `${item.totalPrice} ${item.currencyCode ?? ''}`],
        ] as const
      ).map(([label, value]) => (
        <div key={label} className="gap-phx-1 flex flex-col">
          <span className="text-phx-xs text-phx-text-muted uppercase">{label}</span>
          <span className="text-phx-md">{value}</span>
        </div>
      ))}
    </div>
  ),
  bulkActions: (t) => [
    {
      key: 'export',
      label: t('businessCaseList.bulk.export'),
      run: async (ids) => {
        const columns: ListExportColumn[] = [
          { key: 'number', header: t('businessCaseList.cols.number') },
          { key: 'productName', header: t('businessCaseList.cols.product') },
          { key: 'customerName', header: t('businessCaseList.cols.customer') },
          { key: 'totalPrice', header: t('businessCaseList.cols.totalPrice') },
        ];
        const blob = await exportBusinessCases(
          { filters: [{ field: 'id', ids: ids.map(String) }] },
          columns,
          LIST_EXPORT_MAX_ROWS
        );
        downloadBlob(blob, t('businessCaseList.exportFile'));
      },
    },
  ],
  stats: (t) => [
    {
      key: 'count',
      label: t('businessCaseList.stats.count'),
      value: (s) => STATS_NUMBER.format(s.count),
    },
    {
      key: 'volume',
      label: t('businessCaseList.stats.volume'),
      value: (s) => `${STATS_NUMBER.format(s.totalPrice)} ${t('businessCaseList.currencyUnit')}`,
    },
    {
      key: 'nights',
      label: t('businessCaseList.stats.nights'),
      value: (s) => STATS_NUMBER.format(s.totalNights),
    },
  ],
  columns: (t) => [
    col.mono<BusinessCaseListItem>('number', {
      label: t('businessCaseList.cols.number'),
      w: 80,
      frozen: true,
    }),
    col.strong<BusinessCaseListItem>('productName', {
      label: t('businessCaseList.cols.product'),
      w: 188,
      frozen: true,
    }),
    col.text<BusinessCaseListItem>('customerName', {
      label: t('businessCaseList.cols.customer'),
      w: 158,
    }),
    col.date<BusinessCaseListItem>('dateFrom', {
      label: t('businessCaseList.cols.dateFrom'),
      w: 96,
    }),
    col.date<BusinessCaseListItem>('dateTo', {
      label: t('businessCaseList.cols.dateTo'),
      w: 96,
    }),
    col.num<BusinessCaseListItem>('nights', {
      label: t('businessCaseList.cols.nights'),
      w: 56,
    }),
    col.money<BusinessCaseListItem>('totalPrice', {
      label: t('businessCaseList.cols.totalPrice'),
      w: 128,
      unit: (item) => item.currencyCode,
    }),
    col.text<BusinessCaseListItem>('bcTypeName', {
      label: t('businessCaseList.cols.bcType'),
      w: 110,
      sortKey: 'bcType',
    }),
    col.badge<BusinessCaseListItem>('checkOutStateName', {
      label: t('businessCaseList.cols.checkOutState'),
      w: 124,
      sortKey: 'checkOutState',
    }),
    col.badge<BusinessCaseListItem>('paymentStateName', {
      label: t('businessCaseList.cols.paymentState'),
      w: 124,
      sortKey: 'paymentState',
    }),
    col.text<BusinessCaseListItem>('salesChannelName', {
      label: t('businessCaseList.cols.salesChannel'),
      w: 110,
      sortKey: 'salesChannel',
      visible: false,
    }),
    col.dateTime<BusinessCaseListItem>('creationDate', {
      label: t('businessCaseList.cols.created'),
      w: 116,
      sortKey: 'creationDate',
      visible: false,
    }),
    col.note<BusinessCaseListItem>('note', {
      label: t('businessCaseList.cols.note'),
      w: 240,
      sortable: false,
      // Drahý sloupec — BE poznámku projektuje jen je-li viditelný (request.columns).
      expensive: true,
    }),
  ],
  filterGroups: (t) => [
    { id: 'state', label: t('businessCaseList.groups.state') },
    { id: 'term', label: t('businessCaseList.groups.term') },
    { id: 'business', label: t('businessCaseList.groups.business') },
    { id: 'params', label: t('businessCaseList.groups.params') },
    { id: 'basic', label: t('businessCaseList.groups.basic') },
  ],
  views: (t) => [
    {
      id: 'this-year',
      label: t('businessCaseList.views.thisYear'),
      filters: [
        {
          field: 'dateTo',
          from: `${new Date().getFullYear()}-01-01`,
          to: `${new Date().getFullYear()}-12-31`,
          pinned: true,
        },
      ],
    },
  ],
  filters: (t) => [
    filter.selector<PartnerRef>('customerParty', {
      label: t('businessCaseList.filters.customer'),
      group: 'business',
      freq: true,
      selector: partnerSelector,
      getId: (item) => item.partyId,
      getLabel: (item) => item.name,
    }),
    filter.enum('checkOutState', {
      label: t('businessCaseList.filters.checkOutState'),
      group: 'state',
      freq: true,
      options: codebookOptions((response) => response.checkOutStates),
    }),
    filter.enum('paymentState', {
      label: t('businessCaseList.filters.paymentState'),
      group: 'state',
      options: codebookOptions((response) => response.paymentStates),
    }),
    filter.daterange('dateTo', {
      label: t('businessCaseList.filters.dateTo'),
      group: 'term',
      freq: true,
      presets: [
        {
          label: t('businessCaseList.presets.thisMonth'),
          range: () => {
            const now = new Date();
            return {
              from: isoDate(new Date(now.getFullYear(), now.getMonth(), 1)),
              to: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
            };
          },
        },
        {
          label: t('businessCaseList.presets.nextMonth'),
          range: () => {
            const now = new Date();
            return {
              from: isoDate(new Date(now.getFullYear(), now.getMonth() + 1, 1)),
              to: isoDate(new Date(now.getFullYear(), now.getMonth() + 2, 0)),
            };
          },
        },
        {
          label: t('businessCaseList.presets.thisYear'),
          range: () => {
            const year = new Date().getFullYear();
            return { from: `${year}-01-01`, to: `${year}-12-31` };
          },
        },
      ],
    }),
    filter.daterange('creationDate', {
      label: t('businessCaseList.filters.created'),
      group: 'term',
    }),
    filter.enum('bcType', {
      label: t('businessCaseList.filters.bcType'),
      group: 'business',
      options: codebookOptions((response) => response.bcTypes),
    }),
    filter.enum('salesChannel', {
      label: t('businessCaseList.filters.salesChannel'),
      group: 'business',
      options: codebookOptions((response) => response.salesChannels),
    }),
    filter.number('totalPrice', {
      label: t('businessCaseList.filters.totalPrice'),
      group: 'params',
      freq: true,
      unit: t('businessCaseList.currencyUnit'),
    }),
    filter.text('number', {
      label: t('businessCaseList.filters.number'),
      group: 'basic',
    }),
    filter.text('productName', {
      label: t('businessCaseList.filters.product'),
      group: 'basic',
      freq: true,
    }),
    filter.text('customerName', {
      label: t('businessCaseList.filters.customerText'),
      group: 'basic',
    }),
  ],
});

export const BusinessCaseListPage = businessCaseList.Page;
/** Výběrový režim OP — pro ⋯ akci selectoru (`listPicker: { listPage: BusinessCaseListPicker }`). */
export const BusinessCaseListPicker = businessCaseList.Picker;
